import { useMemo, useEffect } from 'react';

import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

export const useFertilizationCalculations = (
  inputN: string,
  inputP: string,
  inputK: string,
  pricesN: string,
  pricesP: string,
  pricesK: string,
  cleaninputn: string,
  cleaninputp: string,
  cleaninputk: string,
  cleanpricesn: string,
  cleanpricesp: string,
  cleanpricesk: string,
  setEfficiencyN: (value: string) => void,
  setEfficiencyP: (value: string) => void,
  setEfficiencyK: (value: string) => void,
) => {
  const { boardStatusValues } = useCroptimizer();

  const targetNPercent = useMemo(() => {
    if (boardStatusValues?.currentPh) {
      const currentPh = Number(boardStatusValues?.currentPh);
      const targetPh =
        Number(boardStatusValues?.targetPh) > 6.5
          ? 6.5
          : Number(boardStatusValues?.targetPh);

      if (currentPh > 6.5) {
        return '0';
      } else {
        const result =
          1 - (30 * currentPh - 95) / 100 - (1 - (30 * targetPh - 95) / 100);

        return result.toFixed(2);
      }
    }

    return null;
  }, [boardStatusValues]);

  const currentNPercent = useMemo(() => {
    if (targetNPercent !== null) {
      const result = (1 - Number(targetNPercent)) * 100;

      return result;
    }

    return null;
  }, [targetNPercent]);

  const getCurrentN = useMemo(() => {
    if (currentNPercent && inputN) {
      const result = Math.round(
        (Number(currentNPercent) / 100) * Number(cleaninputn),
      );

      return String(result);
    }

    return null;
  }, [currentNPercent, inputN, cleaninputn]);

  const getTargetN = useMemo(() => {
    if (getCurrentN && inputN) {
      const result = Math.round(Number(cleaninputn) - Number(getCurrentN));

      return String(result);
    }

    return null;
  }, [getCurrentN, inputN, cleaninputn]);

  const getTargetPPercent = useMemo(() => {
    if (boardStatusValues?.currentPh) {
      const currentPh = Number(boardStatusValues?.currentPh);
      const targetPh =
        Number(boardStatusValues?.targetPh) > 6.5
          ? 6.5
          : Number(boardStatusValues?.targetPh);

      if (currentPh > 6.5) {
        return '0';
      } else {
        const result =
          1 -
          (35 * currentPh - 127.5) / 100 -
          (1 - (35 * targetPh - 127.5) / 100);

        return result.toFixed(2);
      }
    }

    return null;
  }, [boardStatusValues]);

  const currentPPercent = useMemo(() => {
    if (getTargetPPercent !== null) {
      const result = (1 - Number(getTargetPPercent)) * 100;

      return result;
    }

    return null;
  }, [getTargetPPercent]);

  const getCurrentP = useMemo(() => {
    if (currentPPercent && inputP) {
      const result = Math.round(
        (Number(currentPPercent) / 100) * Number(cleaninputp),
      );

      return String(result);
    }

    return null;
  }, [currentPPercent, inputP, cleaninputp]);

  const getTargetP = useMemo(() => {
    if (getCurrentP && inputP) {
      const result = Math.round(Number(cleaninputp) - Number(getCurrentP));

      return String(result);
    }

    return null;
  }, [getCurrentP, inputP, cleaninputp]);

  const getTargetKPercent = useMemo(() => {
    if (!boardStatusValues?.currentPh) {
      return null;
    }

    const currentPh = Number(boardStatusValues.currentPh);

    if (currentPh > 6) {
      return '0';
    }

    if (!boardStatusValues.targetPh) {
      return null;
    }

    const targetPh = Number(boardStatusValues.targetPh);
    const calculateK = (target: number = 6) => {
      const result =
        1 - (40 * currentPh - 140) / 100 - (1 - (40 * target - 140) / 100);

      return result.toFixed(2);
    };

    if (targetPh < 6.001) {
      return calculateK(targetPh);
    } else {
      return calculateK();
    }
  }, [boardStatusValues]);

  const currentKPercent = useMemo(() => {
    if (getTargetKPercent !== null) {
      const result = (1 - Number(getTargetKPercent)) * 100;

      return result;
    }

    return null;
  }, [getTargetKPercent]);

  const getCurrentK = useMemo(() => {
    if (currentKPercent && inputK) {
      const result = Math.round(
        (Number(currentKPercent) / 100) * Number(cleaninputk),
      );

      return String(result);
    }

    return null;
  }, [currentKPercent, inputK, cleaninputk]);

  const getTargetK = useMemo(() => {
    if (getCurrentK && inputK) {
      const result = Math.round(Number(cleaninputk) - Number(getCurrentK));

      return String(result);
    }

    return null;
  }, [getCurrentK, inputK, cleaninputk]);

  useEffect(() => {
    if (getTargetN && pricesN) {
      const result = Number(getTargetN) * Number(cleanpricesn);
      setEfficiencyN(String(Math.round(result)));
    }
  }, [getTargetN, pricesN, cleanpricesn, setEfficiencyN]);

  useEffect(() => {
    if (getTargetP && pricesP) {
      const result = Number(getTargetP) * Number(cleanpricesp);
      setEfficiencyP(String(Math.round(result)));
    }
  }, [getTargetP, pricesP, cleanpricesp, setEfficiencyP]);

  useEffect(() => {
    if (getTargetK && pricesK) {
      const result = Number(getTargetK) * Number(cleanpricesk);
      setEfficiencyK(String(Math.round(result)));
    }
  }, [getTargetK, pricesK, cleanpricesk, setEfficiencyK]);

  return {
    targetNPercent,
    currentNPercent,
    getCurrentN,
    getTargetN,
    getTargetPPercent,
    currentPPercent,
    getCurrentP,
    getTargetP,
    getTargetKPercent,
    currentKPercent,
    getCurrentK,
    getTargetK,
  };
}; 